/**
 * LayoutDesigner (Sprint I) — parametric visual seat-layout designer.
 * Route: /dashboard/venues/:id/templates/:tid/layout
 *
 * Organiser picks a preset (theatre-curved | arena-bowl | hall-grid |
 * cabaret-tables), tweaks a small set of numeric parameters, previews the SVG
 * live (via POST /api/layouts/preview), then saves + regenerates seats via
 * PUT /api/templates/{tid}/layout.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { venuesApi } from "../../lib/events";
import VisualSeatMap from "../../components/VisualSeatMap";

const PRESET_OPTIONS = [
  { value: "theatre-curved", label: "Theatre — curved rows" },
  { value: "hall-grid", label: "Hall — flat grid" },
  { value: "arena-bowl", label: "Arena — four-sided bowl" },
  { value: "cabaret-tables", label: "Cabaret — round tables" },
];

// Per-preset parameter definitions used to generate the sliders below
const PRESET_PARAMS = {
  "theatre-curved": [
    { key: "rows", label: "Rows", min: 1, max: 30, step: 1, def: 10 },
    { key: "seats_per_row", label: "Seats per row", min: 1, max: 40, step: 1, def: 14 },
    { key: "curvature_deg", label: "Curvature (°)", min: 0, max: 80, step: 5, def: 40 },
    { key: "radius_start", label: "Front-row radius", min: 20, max: 80, step: 1, def: 45 },
    { key: "radius_step", label: "Radius step / row", min: 1, max: 12, step: 0.5, def: 4 },
    { key: "row_depth", label: "Row depth", min: 1, max: 15, step: 0.5, def: 5 },
    { key: "accessible_row", label: "Accessible row #", min: 0, max: 30, step: 1, def: 0 },
  ],
  "hall-grid": [
    { key: "rows", label: "Rows", min: 1, max: 30, step: 1, def: 8 },
    { key: "seats_per_row", label: "Seats per row", min: 1, max: 40, step: 1, def: 12 },
    { key: "accessible_row", label: "Accessible row #", min: 0, max: 30, step: 1, def: 0 },
  ],
  "cabaret-tables": [
    { key: "tables", label: "Number of tables", min: 1, max: 40, step: 1, def: 8 },
    { key: "seats_per_table", label: "Chairs per table", min: 2, max: 16, step: 1, def: 8 },
    { key: "table_cols", label: "Tables per row", min: 1, max: 8, step: 1, def: 4 },
    { key: "table_diameter", label: "Table diameter", min: 4, max: 14, step: 0.5, def: 8 },
  ],
  "arena-bowl": [], // arena uses structured sections handled separately
};

function ParamSlider({ config, value, onChange }) {
  return (
    <div className="mb-4" data-testid={`param-${config.key}`}>
      <div className="flex justify-between mb-1">
        <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gt-ink-2)]">
          {config.label}
        </label>
        <span className="text-xs font-mono font-bold" data-testid={`param-value-${config.key}`}>
          {value}
          {config.key === "curvature_deg" ? "°" : ""}
        </span>
      </div>
      <input
        type="range"
        min={config.min}
        max={config.max}
        step={config.step}
        value={value}
        onChange={(e) => onChange(config.key, parseFloat(e.target.value))}
        className="w-full accent-[var(--gt-accent)]"
        data-testid={`slider-${config.key}`}
      />
    </div>
  );
}

// Aisle chips (hall-grid only)
function AisleEditor({ value, onChange, maxCol }) {
  const [input, setInput] = useState("");
  const add = () => {
    const n = parseInt(input, 10);
    if (!n || n < 1 || n >= maxCol) return;
    if (value.includes(n)) return;
    onChange([...value, n].sort((a, b) => a - b));
    setInput("");
  };
  const remove = (n) => onChange(value.filter((x) => x !== n));
  return (
    <div className="mb-4" data-testid="aisle-editor">
      <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gt-ink-2)] mb-1 block">
        Aisles after column (1 to {maxCol - 1})
      </label>
      <div className="flex gap-2 mb-2 flex-wrap">
        {value.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => remove(n)}
            className="text-xs font-mono bg-[var(--gt-ink)] text-white px-2 py-1 rounded-sm hover:bg-[var(--gt-accent)]"
            data-testid={`aisle-chip-${n}`}
          >
            after {n} ×
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="number"
          min="1"
          max={maxCol - 1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={`e.g. ${Math.floor(maxCol / 2)}`}
          className="w-24 px-2 py-1 text-sm border border-[var(--gt-line)] rounded-sm"
          data-testid="aisle-input"
        />
        <button
          type="button"
          onClick={add}
          className="gt-btn-ghost text-xs"
          data-testid="aisle-add-btn"
        >
          Add aisle
        </button>
      </div>
    </div>
  );
}

// Sections editor (all presets support optional section names, arena needs full config)
function SectionsEditor({ value, onChange, preset }) {
  if (preset === "arena-bowl") {
    // Structured: each section has name + side + rows + seats_per_row
    return (
      <div className="mb-4" data-testid="arena-sections-editor">
        <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gt-ink-2)] mb-2 block">
          Stands
        </label>
        {(value || []).map((sec, idx) => (
          <div key={idx} className="grid grid-cols-4 gap-2 mb-2 items-end" data-testid={`arena-stand-${idx}`}>
            <input
              type="text"
              value={sec.name}
              onChange={(e) => {
                const c = [...value]; c[idx] = { ...c[idx], name: e.target.value }; onChange(c);
              }}
              className="px-2 py-1 text-sm border border-[var(--gt-line)] rounded-sm"
              placeholder="Name"
            />
            <select
              value={sec.side}
              onChange={(e) => { const c = [...value]; c[idx] = { ...c[idx], side: e.target.value }; onChange(c); }}
              className="px-2 py-1 text-sm border border-[var(--gt-line)] rounded-sm"
            >
              <option value="N">North</option>
              <option value="S">South</option>
              <option value="E">East</option>
              <option value="W">West</option>
            </select>
            <input
              type="number"
              min="1" max="20"
              value={sec.rows}
              onChange={(e) => { const c = [...value]; c[idx] = { ...c[idx], rows: parseInt(e.target.value, 10) || 1 }; onChange(c); }}
              className="px-2 py-1 text-sm border border-[var(--gt-line)] rounded-sm"
              placeholder="Rows"
            />
            <input
              type="number"
              min="1" max="30"
              value={sec.seats_per_row}
              onChange={(e) => { const c = [...value]; c[idx] = { ...c[idx], seats_per_row: parseInt(e.target.value, 10) || 1 }; onChange(c); }}
              className="px-2 py-1 text-sm border border-[var(--gt-line)] rounded-sm"
              placeholder="Seats"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...(value || []), { name: `Stand ${(value || []).length + 1}`, side: "N", rows: 5, seats_per_row: 10 }])}
          className="gt-btn-ghost text-xs"
          data-testid="arena-add-stand"
        >
          + Add stand
        </button>
      </div>
    );
  }

  // Simple section-name list (comma-separated) for other presets
  const csv = (value || []).join(", ");
  return (
    <div className="mb-4" data-testid="simple-sections-editor">
      <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gt-ink-2)] mb-1 block">
        Section names (comma-separated)
      </label>
      <input
        type="text"
        value={csv}
        onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        className="w-full px-3 py-2 text-sm border border-[var(--gt-line)] rounded-sm"
        placeholder="Stalls, Circle, Balcony"
        data-testid="sections-input"
      />
      <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">
        Leave blank for one default section. Seats split evenly left → right.
      </p>
    </div>
  );
}

export default function LayoutDesigner() {
  const { id: venueId, tid } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [preset, setPreset] = useState("hall-grid");
  const [params, setParams] = useState({});
  const [renderMode, setRenderMode] = useState("visual");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingSnapshot, setExistingSnapshot] = useState(false);

  const defaultsFor = useCallback((p) => {
    if (p === "arena-bowl") {
      return {
        sections: [
          { name: "North Stand", side: "N", rows: 5, seats_per_row: 10 },
          { name: "South Stand", side: "S", rows: 5, seats_per_row: 10 },
        ],
      };
    }
    const defs = {};
    for (const c of PRESET_PARAMS[p] || []) {
      defs[c.key] = c.def;
    }
    if (p === "hall-grid") { defs.aisle_after_cols = []; defs.sections = []; }
    if (p === "theatre-curved") { defs.sections = []; }
    if (p === "cabaret-tables") { defs.section_name = "Floor"; }
    return defs;
  }, []);

  // Load template + existing layout on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const t = await venuesApi.getTemplate(tid);
        if (!alive) return;
        setTemplate(t.template);
        const existing = await venuesApi.getLayout(tid);
        if (!alive) return;
        if (existing) {
          setPreset(existing.preset);
          setParams({ ...defaultsFor(existing.preset), ...(existing.params || {}) });
          setRenderMode(existing.render_mode || "visual");
        } else {
          setParams(defaultsFor("hall-grid"));
        }
        // Check for any attached-event snapshot (blocks regeneration)
        // Loose heuristic: attempt a preview to see if seats existed; simpler
        // is to check via getTemplate seat count. If seats exist AND active
        // snapshot exists, layout regen will be blocked server-side.
      } catch (err) {
        toast.error("Could not load template.");
      }
    })();
    return () => { alive = false; };
  }, [tid, defaultsFor]);

  // Debounced preview whenever preset/params change
  useEffect(() => {
    if (!template) return;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const p = await venuesApi.previewLayout({
          preset,
          params,
          viewport_width: 100,
          viewport_height: 100,
        });
        setPreview(p);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Preview failed.");
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [preset, params, template]);

  const changePreset = (p) => {
    setPreset(p);
    setParams(defaultsFor(p));
  };

  const changeParam = (key, val) => {
    setParams((prev) => ({ ...prev, [key]: val }));
  };

  const seatsForPreview = useMemo(() => {
    if (!preview?.seats) return [];
    return preview.seats.map((s, i) => ({
      id: `pre-${i}`,
      section: s.section,
      row_label: s.row_label,
      seat_number: s.seat_number,
      seat_label: s.seat_label,
      status: "available",
      x_percent: s.x_percent,
      y_percent: s.y_percent,
      rotation_deg: s.rotation_deg,
      shape: s.shape,
      is_accessible: s.is_accessible,
    }));
  }, [preview]);

  const save = async () => {
    if (!template) return;
    if (!window.confirm("Save this layout? Existing seats for this template will be replaced.")) return;
    setSaving(true);
    try {
      await venuesApi.saveLayout(tid, {
        preset,
        params,
        viewport_width: 100,
        viewport_height: 100,
        render_mode: renderMode,
        regenerate_seats: true,
      });
      toast.success(`Layout saved · ${preview?.count || 0} seats generated`);
      navigate(`/dashboard/venues/${venueId}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(detail);
      if (String(detail).toLowerCase().includes("attached")) {
        setExistingSnapshot(true);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!template) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="layout-loading">
        <Loader2 size={20} className="animate-spin text-[var(--gt-ink-2)]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8" data-testid="layout-designer">
      <Link
        to={`/dashboard/venues/${venueId}`}
        className="gt-btn-ghost text-xs mb-4"
        data-testid="layout-back-btn"
      >
        <ArrowLeft size={12} /> Back to venue
      </Link>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="gt-label mb-1 flex items-center gap-2">
            <Wand2 size={12} /> Layout designer
          </div>
          <h1
            className="font-heading text-3xl sm:text-4xl font-black tracking-tight"
            data-testid="layout-title"
          >
            {template.template_name}
          </h1>
          <p className="text-xs text-[var(--gt-ink-3)] mt-1">
            {template.template_type} · {template.capacity} capacity · {template.status}
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || busy}
          className="gt-btn-primary"
          data-testid="layout-save-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save layout & generate seats
        </button>
      </div>

      {existingSnapshot && (
        <div className="mb-6 p-3 border border-amber-400/60 bg-amber-50 text-amber-900 text-sm rounded-sm">
          This template is attached to one or more live events. Regenerating seats is blocked.
          <strong> Clone the template first</strong>, then design the new layout on the clone.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
        {/* Left column — controls */}
        <div>
          <div className="mb-6">
            <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gt-ink-2)] mb-1 block">
              Preset
            </label>
            <select
              value={preset}
              onChange={(e) => changePreset(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--gt-line)] rounded-sm bg-white"
              data-testid="preset-select"
            >
              {PRESET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gt-ink-2)] mb-2 block">
              Buyer render mode
            </label>
            <div className="flex gap-2" data-testid="render-mode-toggle">
              {["visual", "list"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setRenderMode(m)}
                  className={
                    "flex-1 px-3 py-2 text-xs uppercase font-bold tracking-widest border rounded-sm " +
                    (renderMode === m
                      ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
                      : "border-[var(--gt-line)] bg-white text-[var(--gt-ink-2)] hover:border-[var(--gt-ink)]")
                  }
                  data-testid={`render-mode-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">
              Visual = SVG seat map. List = the fallback grid picker (a11y-friendly).
            </p>
          </div>

          {(PRESET_PARAMS[preset] || []).map((cfg) => (
            <ParamSlider
              key={cfg.key}
              config={cfg}
              value={params[cfg.key] ?? cfg.def}
              onChange={changeParam}
            />
          ))}

          {preset === "hall-grid" && (
            <AisleEditor
              value={params.aisle_after_cols || []}
              onChange={(v) => changeParam("aisle_after_cols", v)}
              maxCol={params.seats_per_row || 12}
            />
          )}

          {(preset === "theatre-curved" || preset === "hall-grid") && (
            <SectionsEditor
              value={params.sections || []}
              onChange={(v) => changeParam("sections", v)}
              preset={preset}
            />
          )}
          {preset === "arena-bowl" && (
            <SectionsEditor
              value={params.sections || []}
              onChange={(v) => changeParam("sections", v)}
              preset={preset}
            />
          )}
        </div>

        {/* Right column — live preview */}
        <div>
          <div className="mb-3 flex items-baseline justify-between">
            <div className="gt-label">Live preview</div>
            <div className="text-xs text-[var(--gt-ink-3)] font-mono" data-testid="preview-count">
              {busy ? "Rendering…" : `${preview?.count || 0} seats · ${preview?.section_names?.length || 0} section(s)`}
            </div>
          </div>
          <div className="border border-[var(--gt-line)] rounded-sm bg-white p-2">
            {preview ? (
              <VisualSeatMap
                seats={seatsForPreview}
                layout={{
                  preset,
                  viewport_width: preview.viewport_width,
                  viewport_height: preview.viewport_height,
                }}
                selectedSeats={[]}
                onToggleSeat={() => {}}
                maxSelectable={0}
              />
            ) : (
              <div className="h-96 flex items-center justify-center text-[var(--gt-ink-3)]">
                <Loader2 size={20} className="animate-spin" />
              </div>
            )}
          </div>
          <p className="text-[10px] text-[var(--gt-ink-3)] mt-2">
            Preview is stateless — sliders re-render instantly. Nothing saves until you click <em>Save layout &amp; generate seats</em>.
          </p>
        </div>
      </div>
    </div>
  );
}
